import { OfflineDatabaseKit } from '../../../../../../../database/offlineDatabase/types/offlineDatabaseKit.types';
import { offlineKitsRepository } from '../../../../../../../repositories/offlineKits.repository';
import { QueryKeysAndFnOptions } from '../../../useQueryKeysAndFnToCache.types';
import { generateQueryKeysAndFnToPaginatedEndpoint } from '../../generateQueryKeysAndFnToPaginatedEndpoint';
import { QueryKeysAndFnGenerator, QueryKeysAndFnGeneratorParams } from './types';
import { FindKitsToOfflineOutput } from './types/kitsQueryKeysAndFnGenerator.types';
import { OfflineImageCacher } from './utils/OfflineImageCacher.util';

export class KitsQueryKeysAndFnGenerator implements QueryKeysAndFnGenerator {
  private readonly IMAGES_CHUNK_SIZE = 10;
  private readonly MAX_IMAGE_CACHE_SIZE_100_MB_IN_BYTES = 100 * 1024 * 1024;
  private readonly offlineImageCacher: OfflineImageCacher;
  constructor(
    private readonly params: Pick<QueryKeysAndFnGeneratorParams, 'priority' | 'webpEnabled'>,
  ) {
    this.offlineImageCacher = new OfflineImageCacher(
      this.MAX_IMAGE_CACHE_SIZE_100_MB_IN_BYTES,
      this.IMAGES_CHUNK_SIZE,
      this.params.webpEnabled,
    );
  }

  async generateQueryKeysAndFn(): Promise<QueryKeysAndFnOptions> {
    return generateQueryKeysAndFnToPaginatedEndpoint<FindKitsToOfflineOutput>({
      url: '/kits/offline/v1',
      priority: this.params.priority,
      fnToSaveData: async (data) => this.handleSaveAll(data),
    });
  }

  private async handleSaveAll(data: FindKitsToOfflineOutput[]): Promise<void> {
    const offlineKits = await this.getOfflineKitsToSave(data);
    await offlineKitsRepository.saveAll(offlineKits);
  }

  private async getOfflineKitsToSave(
    data: FindKitsToOfflineOutput[],
  ): Promise<OfflineDatabaseKit[]> {
    const kitsWithCachedImages = await this.cacheKitImages(data);
    const offlineKits = kitsWithCachedImages.map((kit) => this.transformDataToOfflineKit(kit));

    return offlineKits;
  }

  private async cacheKitImages(
    data: FindKitsToOfflineOutput[],
  ): Promise<FindKitsToOfflineOutput[]> {
    const imageUrlsToCache = data
      .filter((kit) => kit.imageUrl !== null)
      .map((kit) => ({ id: kit.id, url: kit.imageUrl as string }));

    const { successItemsMap } = await this.offlineImageCacher.cacheImages(imageUrlsToCache, 'md');

    const updatedData = data.map((kit) => {
      const original = kit.imageUrl;
      if (!original) {
        return kit;
      }

      const cached = successItemsMap.get(kit.id);

      return {
        ...kit,
        imageUrl: cached ?? original,
      };
    });

    return updatedData;
  }

  private transformDataToOfflineKit(data: FindKitsToOfflineOutput): OfflineDatabaseKit {
    return {
      id: data.id,
      name: data.name,
      code: data.code,
      type: data.type,
      active: data.active,
      resume: data.resume,
      imageUrl: data.imageUrl,
      description: data.description,
      wholesalePrice: data.wholesalePrice,
      availableAmount: data.availableAmount,
      skuKits: data.skuKits.map((skuKit) => ({
        id: skuKit.id,
        quantity: skuKit.quantity,
        availability: skuKit.availability,
        deliveryWindow: skuKit.deliveryWindowId ? { id: skuKit.deliveryWindowId } : null,
        sku: {
          id: skuKit.skuId,
          futureStock: this.mountSkuKitFutureStock(skuKit.futureStockId),
        },
        productColor: {
          id: skuKit.productColorId,
        },
        product: {
          id: skuKit.productId,
        },
      })),
    };
  }

  private mountSkuKitFutureStock(futureStockId: string | null) {
    if (!futureStockId) {
      return null;
    }

    return {
      id: futureStockId,
    };
  }
}
